import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../Firebase/firebaseconfig';
import logoSrc from '../Assets/Square_logo.jpeg';
import './AdminLogin.css';

const ERROR_MAP = {
  'auth/wrong-password': 'Incorrect password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/invalid-email': 'Invalid email address.',
  'auth/invalid-phone-number': 'Invalid phone number. Include country code (e.g. +91...).',
  'auth/invalid-verification-code': 'Invalid OTP. Please try again.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Check your connection.',
  'auth/operation-not-allowed': 'Phone sign-in is disabled for this project. Enable it in Firebase Console → Authentication → Sign-in method → Phone.',
  'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please refresh the page and try again.',
  'auth/missing-app-credential': 'App verification failed. Please refresh the page and try again.',
  'auth/quota-exceeded': 'SMS quota exceeded for this project. Please try again later.',
  'auth/app-not-authorized': 'This domain is not authorized for phone sign-in. Add it in Firebase Console → Authentication → Settings → Authorized domains.',
  'auth/internal-error': 'Internal authentication error. Please refresh the page and try again.',
  'auth/billing-not-enabled': 'Phone sign-in requires the Blaze (pay-as-you-go) plan on this Firebase project.',
  'auth/unauthorized-domain': 'This domain is not authorized for phone sign-in. Add it in Firebase Console → Authentication → Settings → Authorized domains.',
};

function friendlyError(code) {
  return ERROR_MAP[code] || `Something went wrong${code ? ` (${code})` : ''}. Please try again.`;
}

// Phone sign-in is parked until Firebase phone auth is fully configured/tested — flip this on later.
const PHONE_AUTH_ENABLED = false;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneStep, setPhoneStep] = useState('enter');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const recaptchaRef = useRef(null);
  const verifierRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) navigate('/admin/dashboard', { replace: true });
    });
    return unsub;
  }, [navigate]);

  const resetRecaptcha = () => {
    if (verifierRef.current) {
      try { verifierRef.current.clear(); } catch {}
      verifierRef.current = null;
    }
  };

  const setupRecaptcha = () => {
    if (!verifierRef.current) {
      verifierRef.current = new RecaptchaVerifier(auth, recaptchaRef.current, {
        size: 'invisible',
      });
    }
    return verifierRef.current;
  };

  const fullPhone = '+91' + phone;

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      localStorage.setItem('adminLoginAt', String(Date.now()));
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{10}$/.test(phone)) {
      setError('Enter a valid 10-digit phone number.');
      return;
    }
    setLoading(true);
    try {
      const verifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, fullPhone, verifier);
      setConfirmationResult(result);
      setPhoneStep('verify');
    } catch (err) {
      console.error('Phone OTP send failed:', err);
      setError(friendlyError(err.code));
      resetRecaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmationResult.confirm(otp);
      localStorage.setItem('adminLoginAt', String(Date.now()));
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (t) => {
    setTab(t);
    setError('');
    setPhoneStep('enter');
    setOtp('');
    resetRecaptcha();
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-brand">
          <img src={logoSrc} alt="Square Computers" className="admin-brand-logo" />
          <h1>Square Computers</h1>
          <p>Admin Portal</p>
        </div>

        {PHONE_AUTH_ENABLED && (
          <div className="admin-tab-switcher">
            <div className={`admin-tab-indicator ${tab === 'phone' ? 'phone' : ''}`} />
            <button className={tab === 'email' ? 'active' : ''} onClick={() => switchTab('email')}>
              Email
            </button>
            <button className={tab === 'phone' ? 'active' : ''} onClick={() => switchTab('phone')}>
              Phone
            </button>
          </div>
        )}

        {error && <p className="admin-error">{error}</p>}

        <div key={tab + phoneStep} className="admin-form-fade">
          {tab === 'email' && (
            <form onSubmit={handleEmailLogin} className="admin-form">
              <label htmlFor="admin-email">Email Address</label>
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <label htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button type="submit" className="admin-submit-btn" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
          )}

          {PHONE_AUTH_ENABLED && tab === 'phone' && phoneStep === 'enter' && (
            <form onSubmit={handleSendOtp} className="admin-form">
              <label htmlFor="admin-phone">Phone Number</label>
              <div className="admin-phone-input-wrap">
                <span className="admin-phone-prefix">+91</span>
                <input
                  id="admin-phone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="98765 43210"
                  required
                />
              </div>
              <p className="admin-hint">Enter your 10-digit number, country code is fixed to +91</p>
              <div ref={recaptchaRef} />
              <button type="submit" className="admin-submit-btn" disabled={loading}>
                {loading ? 'Sending OTP…' : 'Send OTP'}
              </button>
            </form>
          )}

          {PHONE_AUTH_ENABLED && tab === 'phone' && phoneStep === 'verify' && (
            <form onSubmit={handleVerifyOtp} className="admin-form">
              <p className="admin-otp-sent">OTP sent to {fullPhone}</p>
              <label htmlFor="admin-otp">Enter OTP</label>
              <input
                id="admin-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value)}
                placeholder="6-digit code"
                required
                autoFocus
              />
              <button type="submit" className="admin-submit-btn" disabled={loading}>
                {loading ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <button
                type="button"
                className="admin-back-link"
                onClick={() => { setPhoneStep('enter'); setOtp(''); setError(''); resetRecaptcha(); }}
              >
                ← Change number
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
